import { css, useTheme } from "@emotion/react";
import { omit } from "lodash/fp";
import { useContext, useState, Fragment } from "react";

import {
  Icon,
  useThemeProps,
  TypographyBlock,
  withDefaultProps,
  Accordion,
  Box,
  Heading,
  Section,
  backgroundColorContext,
  generateUniqueId,
  InlineList,
  Button,
} from "../../wsui/base";

import PostsModuleArchiveLink from "./PostsModuleArchiveLink.jsx";
import postsModuleContext from "./postsModuleContext";

/**
 * Uses item title and content instead of relying on visibleFields
 */
export default function PostsModuleExpandableListLayout(props) {
  props = useThemeProps({
    props,
    name: "MunicipioPostsModuleExpandableListLayout",
  });
  let {
    title,
    titleIcon,
    titleIconProps = { size: 8 },
    description,
    items,
    spacing = 4,
    headingVariant,
    archiveLink,
    archiveLinkProps,
    headingId,
    toggleIconProps,
    align,
    ...restProps
  } = omit(["gridMaxColumns"], props);
  const contentAlign =
    align === "left" || align === "center" || align === "right"
      ? align
      : undefined;
  const inlineListAlign =
    contentAlign === "left"
      ? "start"
      : contentAlign === "right"
        ? "end"
        : contentAlign === "center"
          ? "center"
          : undefined;
  const archiveAlign =
    contentAlign === "right"
      ? "flex-end"
      : contentAlign === "center"
        ? "center"
        : "flex-start";

  // eslint-disable-next-line no-unused-vars
  const theme = useTheme();

  let ownerState = useContext(postsModuleContext);
  let { itemColor, autoItemColor } = ownerState;

  let containerBackgroundColor = useContext(backgroundColorContext);
  const isBackgroundWhite = containerBackgroundColor === "#ffffff";
  if (itemColor === "auto") {
    itemColor = isBackgroundWhite ? autoItemColor : "white";
  }

  const [defaltHeadingId] = useState(
    () => `module-heading-${generateUniqueId()}`,
  );
  headingId = headingId || defaltHeadingId;

  const MaybeSection = title ? Section : Fragment;

  return (
    <div {...restProps}>
      <div
        css={css`
          display: grid;
          grid-template-columns: 1fr max-content;
          align-items: start;
        `}
      >
        {title && (
          <Heading
            variant={headingVariant}
            marginAfter
            id={headingId}
            css={css`
              display: flex;
              gap: 0.5em;
              align-items: start;
            `}
          >
            {titleIcon && (
              <Icon
                name={titleIcon}
                css={css`
                  align-self: center;
                  flex: none;
                `}
                {...titleIconProps}
              />
            )}
            {title}
          </Heading>
        )}

        {!!(archiveLink && title) && (
          <PostsModuleArchiveLink
            align={archiveAlign}
            {...archiveLink}
            {...archiveLinkProps}
          />
        )}
      </div>
      {description && (
        <TypographyBlock marginAfter align="left">{description}</TypographyBlock>
      )}
      <MaybeSection>
        <Accordion
          spacing={spacing}
          marginAfter={!!(archiveLink && !title)}
          components={{
            Item: {
              as: Box,
              borderRadius: 2,
              color: itemColor,
              style: {
                [[
                  "&.wsui-is-expanded",
                  "&:has(.wsui-details-summary-toggle.wsui-is-interactive:hover)",
                  "&:has(.wsui-details-summary-toggle.wsui-is-interactive:focus-visible)",
                ]]: {
                  backgroundColor: itemColor ? [itemColor, "hover"] : undefined,
                },
              },
              components: {
                Summary: withDefaultProps(Heading, {
                  style: { maxWidth: "none" },
                  align: contentAlign,
                }),
                SummaryToggleIcon: {
                  size: 7.5,
                  ...toggleIconProps,
                },
              },
            },
          }}
          items={items.map(({ content, link, ...item }) => ({
            ...item,
            content: (
              <Fragment>
                <TypographyBlock align={contentAlign} marginAfter={!!link?.title}>
                  {content}
                </TypographyBlock>
                {!!link?.title && (
                  <InlineList align={inlineListAlign}>
                    <Button {...link} variant="primary" />
                  </InlineList>
                )}
              </Fragment>
            ),
          }))}
        />
        {!!(archiveLink && !title) && (
          <PostsModuleArchiveLink
            align={archiveAlign}
            {...archiveLink}
            {...archiveLinkProps}
          />
        )}
      </MaybeSection>
    </div>
  );
}
